'use client';

import {
  fetchContentLibrary,
  useContentLibraryItems,
  useContentLibraryItemSchema,
} from '@commenda/api/content-library';
import {
  Alert,
  Badge,
  Button,
  Input,
  Label,
  LoadingIndicator,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@commenda/kakashi';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ContentLibraryConfig, ContentLibraryInput, DataSourceConfig, FormDSL } from '@commenda/types';
import { extractFieldPaths, StaticArrayInput } from './StaticArrayInput';

// Preview helper — fetches and displays options based on static inputs
function ContentLibraryPreview({
  contentId,
  inputs,
  schemaInputs,
  onOptionsLoaded,
}: {
  contentId: string;
  inputs: ContentLibraryInput[];
  schemaInputs?: { key: string; required: boolean }[];
  onOptionsLoaded?: (options: Record<string, unknown>[]) => void;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState<
    { value: string; label: string; description?: string }[]
  >([]);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchedKey, setLastFetchedKey] = useState<string | null>(null);

  const isInputRequired = useCallback(
    (key: string) => {
      const schemaInput = schemaInputs?.find((s) => s.key === key);
      return schemaInput?.required ?? false;
    },
    [schemaInputs]
  );

  // All required inputs must have static values (optional inputs can be empty)
  const canFetch = useMemo(
    () =>
      inputs.every((input) => {
        const required = isInputRequired(input.key);

        if (input.source === 'static') {
          const val = input.value;
          const hasValue = Array.isArray(val)
            ? val.length > 0
            : val !== undefined && val !== null && val !== '';
          return required ? hasValue : true;
        }
        return !required;
      }),
    [inputs, isInputRequired]
  );

  const hasNonStaticInputs = useMemo(
    () => inputs.some((input) => input.source !== 'static'),
    [inputs]
  );

  const fetchPreview = useCallback(async () => {
    if (!canFetch) return;

    const body: Record<string, unknown> = {};
    inputs.forEach((input) => {
      if (input.source !== 'static') return;
      const val = input.value;
      const isEmpty = Array.isArray(val)
        ? val.length === 0
        : val === undefined || val === null || val === '';
      if (isEmpty) return;

      if (Array.isArray(val)) {
        body[input.key] = val;
        return;
      }
      const strVal = String(val);
      const numVal = Number(strVal);
      body[input.key] =
        !Number.isNaN(numVal) && strVal.trim() !== '' ? numVal : strVal;
    });

    const fetchKey = `${contentId}:${JSON.stringify(body)}`;
    if (fetchKey === lastFetchedKey) return;

    setIsLoading(true);
    setError(null);

    const result = await fetchContentLibrary(contentId, body);

    if (result.success && result.data) {
      const fetchedOptions = result.data.options || [];
      setOptions(fetchedOptions);
      setLastFetchedKey(fetchKey);
      setError(null);
      onOptionsLoaded?.(fetchedOptions as unknown as Record<string, unknown>[]);
    } else {
      setError(result.error || 'Failed to fetch options');
      setOptions([]);
      onOptionsLoaded?.([]);
    }

    setIsLoading(false);
  }, [contentId, inputs, canFetch, lastFetchedKey, onOptionsLoaded]);

  useEffect(() => {
    if (canFetch && isExpanded) {
      fetchPreview();
    }
  }, [canFetch, isExpanded, fetchPreview]);

  return (
    <div className="space-y-2 border-t border-border-secondary pt-4 mt-4">
      <button
        type="button"
        className="flex items-center gap-2 w-full text-left"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {isExpanded ? (
          <ChevronDown className="w-4 h-4 text-text-tertiary" />
        ) : (
          <ChevronRight className="w-4 h-4 text-text-tertiary" />
        )}
        <span className="txt-small-medium text-text-secondary">
          Preview Options
        </span>
        {options.length > 0 && (
          <Badge variant="gray" className="ml-auto">
            {options.length} options
          </Badge>
        )}
      </button>

      {isExpanded && (
        <div className="pl-6 space-y-3">
          {hasNonStaticInputs && (
            <Alert
              variant="info"
              description="Some inputs are bound to workflow context or form fields. Preview only works when all inputs are static values."
            />
          )}

          {canFetch && (
            <>
              <Button
                variant="secondary"
                size="sm"
                onClick={fetchPreview}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <LoadingIndicator size={16} />
                    <span className="ml-2">Fetching...</span>
                  </>
                ) : (
                  'Fetch Options'
                )}
              </Button>

              {error && <Alert variant="destructive" description={error} />}

              {options.length > 0 && (
                <div className="border border-border-secondary rounded-md max-h-48 overflow-y-auto">
                  {options.slice(0, 20).map((option, index) => (
                    <div
                      key={option.value || index}
                      className="px-3 py-2 border-b border-border-secondary last:border-b-0"
                    >
                      <p className="txt-small-medium text-text-primary">
                        {option.label}
                      </p>
                      {option.description && (
                        <p className="txt-xs-regular text-text-tertiary">
                          {option.description}
                        </p>
                      )}
                      <p className="txt-xs-regular text-text-quaternary">
                        value: {option.value}
                      </p>
                    </div>
                  ))}
                  {options.length > 20 && (
                    <div className="px-3 py-2 text-center txt-xs-regular text-text-tertiary">
                      ... and {options.length - 20} more options
                    </div>
                  )}
                </div>
              )}

              {options.length === 0 &&
                !isLoading &&
                !error &&
                lastFetchedKey && (
                  <p className="txt-xs-regular text-text-tertiary">
                    No options returned from API.
                  </p>
                )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export function ContentLibraryEditor({
  dataSource,
  onChange,
  dsl,
  currentFieldId,
}: {
  dataSource?: DataSourceConfig;
  onChange: (dataSource: DataSourceConfig | undefined) => void;
  dsl?: FormDSL;
  currentFieldId?: string;
}) {
  const contentLibrary = dataSource?.contentLibrary;

  const { data: contentItemsData, isLoading: isLoadingItems } =
    useContentLibraryItems();

  const { data: contentItemSchema, isLoading: isLoadingSchema } =
    useContentLibraryItemSchema(contentLibrary?.contentId || null);

  const lastContentIdRef = useRef<string | null>(null);

  const [previewOptions, setPreviewOptions] = useState<
    Record<string, unknown>[]
  >([]);

  const availableFieldPaths = useMemo(() => {
    if (previewOptions.length === 0) return [];
    const sampleOption = previewOptions[0];
    return extractFieldPaths(sampleOption);
  }, [previewOptions]);

  const updateContentLibrary = (updates: Partial<ContentLibraryConfig>) => {
    onChange({
      ...dataSource,
      type: 'content-library',
      contentLibrary: {
        contentId: '',
        inputs: [],
        ...contentLibrary,
        ...updates,
      },
    });
  };

  // Auto-populate inputs when content item changes or schema loads, but only
  // when there are no existing inputs (don't overwrite saved data).
  useEffect(() => {
    if (
      contentItemSchema?.inputs &&
      contentLibrary?.contentId &&
      lastContentIdRef.current !== contentLibrary.contentId
    ) {
      lastContentIdRef.current = contentLibrary.contentId;

      const hasExistingInputs =
        contentLibrary.inputs && contentLibrary.inputs.length > 0;
      if (!hasExistingInputs) {
        const autoInputs: ContentLibraryInput[] = contentItemSchema.inputs.map(
          (schemaInput) => ({
            key: schemaInput.key,
            source: 'context' as const,
            contextPath: schemaInput.key,
            expectedType: schemaInput.type,
          })
        );

        updateContentLibrary({ inputs: autoInputs });
      } else {
        const inputsNeedTypeUpdate = contentLibrary.inputs.some(
          (input) => !input.expectedType
        );
        if (inputsNeedTypeUpdate) {
          const updatedInputs = contentLibrary.inputs.map((input) => {
            const schemaInput = contentItemSchema.inputs.find(
              (s) => s.key === input.key
            );
            return schemaInput?.type && !input.expectedType
              ? { ...input, expectedType: schemaInput.type }
              : input;
          });
          updateContentLibrary({ inputs: updatedInputs });
        }
      }
    }
  }, [contentItemSchema, contentLibrary?.contentId, contentLibrary?.inputs]);

  const updateInput = (
    index: number,
    updates: Partial<ContentLibraryInput>
  ) => {
    const inputs = [...(contentLibrary?.inputs ?? [])];
    inputs[index] = { ...inputs[index], ...updates };
    updateContentLibrary({ inputs });
  };

  const availableFields = useMemo(() => {
    if (!dsl) return [];
    const fields: { fieldId: string; label: string }[] = [];
    (dsl as FormDSL).steps?.forEach((step) =>
      step.modules?.forEach((mod) =>
        mod.components?.forEach((comp) => {
          if (comp.fieldId && comp.fieldId !== currentFieldId) {
            fields.push({
              fieldId: comp.fieldId,
              label: comp.label || comp.fieldId,
            });
          }
        })
      )
    );
    return fields;
  }, [dsl, currentFieldId]);

  const formContextKeys = useMemo(() => {
    const requirements = (
      dsl as unknown as {
        form?: { contextRequirements?: { required?: string[] } };
      }
    )?.form?.contextRequirements;
    return requirements?.required || [];
  }, [dsl]);

  const contentItems = contentItemsData?.items || [];

  const getSchemaInput = (key: string) =>
    contentItemSchema?.inputs?.find((i) => i.key === key);

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Content Library Item</Label>
        <Select
          value={contentLibrary?.contentId ?? ''}
          onValueChange={(value) => {
            lastContentIdRef.current = null;
            setPreviewOptions([]);
            updateContentLibrary({
              contentId: value,
              inputs: [],
              mapping: undefined,
            });
          }}
          disabled={isLoadingItems}
        >
          <SelectTrigger>
            {isLoadingItems ? (
              <div className="flex items-center gap-2">
                <LoadingIndicator size={16} />
                <span>Loading...</span>
              </div>
            ) : (
              <SelectValue placeholder="Select content item..." />
            )}
          </SelectTrigger>
          <SelectContent>
            {contentItems.map((item) => (
              <SelectItem key={item.id} value={item.id}>
                {item.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {contentLibrary?.contentId && contentItemSchema?.description && (
          <p className="txt-xs-regular text-text-tertiary">
            {contentItemSchema.description}
          </p>
        )}
      </div>

      {contentLibrary?.contentId && (
        <div className="space-y-3">
          <Label>Inputs</Label>

          {isLoadingSchema ? (
            <div className="flex items-center justify-center py-4">
              <LoadingIndicator size={20} />
            </div>
          ) : (
            <>
              {(contentLibrary.inputs || []).map((input, index) => {
                const schemaInput = getSchemaInput(input.key);
                const isRequired = schemaInput?.required ?? false;

                return (
                  <div
                    key={input.key}
                    className={`p-3 border rounded-md space-y-2 ${
                      isRequired
                        ? 'border-border-primary bg-bg-secondary'
                        : 'border-border-secondary'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="txt-small-medium text-text-primary">
                          {schemaInput?.label || input.key}
                        </span>
                        {isRequired && (
                          <Badge variant="gray" className="text-xs">
                            Required
                          </Badge>
                        )}
                      </div>
                    </div>

                    {schemaInput?.description && (
                      <p className="txt-xs-regular text-text-tertiary">
                        {schemaInput.description}
                      </p>
                    )}

                    <Select
                      value={input.source}
                      onValueChange={(value) =>
                        updateInput(index, {
                          source: value as 'static' | 'context' | 'field',
                          contextPath:
                            value === 'context' ? input.key : undefined,
                          fieldId: undefined,
                          value: undefined,
                        })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="context">
                          From Workflow Context
                        </SelectItem>
                        <SelectItem value="field">
                          From Another Field
                        </SelectItem>
                        <SelectItem value="static">Static Value</SelectItem>
                      </SelectContent>
                    </Select>

                    {input.source === 'context' && (
                      <Select
                        value={input.contextPath ?? ''}
                        onValueChange={(value) =>
                          updateInput(index, { contextPath: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select context key..." />
                        </SelectTrigger>
                        <SelectContent>
                          {formContextKeys.length > 0 ? (
                            formContextKeys.map((key) => (
                              <SelectItem key={key} value={key}>
                                {key}
                              </SelectItem>
                            ))
                          ) : (
                            <>
                              <SelectItem value="corporationId">
                                corporationId
                              </SelectItem>
                              <SelectItem value="companyId">
                                companyId
                              </SelectItem>
                              <SelectItem value="jurisdictionId">
                                jurisdictionId
                              </SelectItem>
                            </>
                          )}
                        </SelectContent>
                      </Select>
                    )}

                    {input.source === 'field' && (
                      <Select
                        value={input.fieldId ?? ''}
                        onValueChange={(value) =>
                          updateInput(index, { fieldId: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select field..." />
                        </SelectTrigger>
                        <SelectContent>
                          {availableFields.map((field) => (
                            <SelectItem
                              key={field.fieldId}
                              value={field.fieldId}
                            >
                              {field.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}

                    {input.source === 'static' &&
                      schemaInput?.type === 'string[]' && (
                        <StaticArrayInput
                          value={input.value as string[] | undefined}
                          onChange={(arr) => updateInput(index, { value: arr })}
                          placeholder="Enter value..."
                        />
                      )}

                    {input.source === 'static' &&
                      schemaInput?.type !== 'string[]' && (
                        <Input
                          value={String(input.value ?? '')}
                          onChange={(e) =>
                            updateInput(index, { value: e.target.value })
                          }
                          placeholder={`Enter ${schemaInput?.type || 'value'}...`}
                        />
                      )}
                  </div>
                );
              })}

              {(!contentLibrary.inputs ||
                contentLibrary.inputs.length === 0) && (
                <p className="txt-xs-regular text-text-tertiary text-center py-2">
                  No inputs defined for this content item.
                </p>
              )}
            </>
          )}
        </div>
      )}

      {/* Preview Section - Test the configuration */}
      {contentLibrary?.contentId &&
        contentLibrary.inputs &&
        contentLibrary.inputs.length > 0 && (
          <ContentLibraryPreview
            contentId={contentLibrary.contentId}
            inputs={contentLibrary.inputs}
            schemaInputs={contentItemSchema?.inputs}
            onOptionsLoaded={setPreviewOptions}
          />
        )}

      {/* Field Mapping Section - Only show after preview has loaded options */}
      {contentLibrary?.contentId && availableFieldPaths.length > 0 && (
        <div className="space-y-3 border-t border-border-secondary pt-4 mt-4">
          <div className="flex items-center justify-between">
            <Label>Field Mapping</Label>
            <p className="txt-xs-regular text-text-quaternary">Optional</p>
          </div>
          <p className="txt-xs-regular text-text-tertiary">
            Map API response fields to component option fields. Leave empty to
            use defaults.
          </p>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Label className="w-24 shrink-0 txt-xs-medium">Value</Label>
              <Select
                value={contentLibrary.mapping?.value || '__default__'}
                onValueChange={(val) =>
                  updateContentLibrary({
                    mapping: {
                      ...contentLibrary.mapping,
                      value: val === '__default__' ? undefined : val,
                    },
                  })
                }
              >
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="value (default)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__default__">value (default)</SelectItem>
                  {availableFieldPaths.map((path) => (
                    <SelectItem key={path} value={path}>
                      {path}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Label className="w-24 shrink-0 txt-xs-medium">Label</Label>
              <Select
                value={contentLibrary.mapping?.label || '__default__'}
                onValueChange={(val) =>
                  updateContentLibrary({
                    mapping: {
                      ...contentLibrary.mapping,
                      label: val === '__default__' ? undefined : val,
                    },
                  })
                }
              >
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="label (default)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__default__">label (default)</SelectItem>
                  {availableFieldPaths.map((path) => (
                    <SelectItem key={path} value={path}>
                      {path}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Label className="w-24 shrink-0 txt-xs-medium">Description</Label>
              <Select
                value={contentLibrary.mapping?.description || '__default__'}
                onValueChange={(val) =>
                  updateContentLibrary({
                    mapping: {
                      ...contentLibrary.mapping,
                      description: val === '__default__' ? undefined : val,
                    },
                  })
                }
              >
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="description (default)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__default__">
                    description (default)
                  </SelectItem>
                  {availableFieldPaths.map((path) => (
                    <SelectItem key={path} value={path}>
                      {path}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Label className="w-24 shrink-0 txt-xs-medium">Disabled</Label>
              <Select
                value={contentLibrary.mapping?.disabled || '__none__'}
                onValueChange={(val) =>
                  updateContentLibrary({
                    mapping: {
                      ...contentLibrary.mapping,
                      disabled: val === '__none__' ? undefined : val,
                    },
                  })
                }
              >
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {availableFieldPaths.map((path) => (
                    <SelectItem key={path} value={path}>
                      {path}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
